import { ComponentChildren, h, render } from "preact";
import { RoutableProps } from "preact-router";
import { useEffect, useState } from "preact/hooks";
import { ApiError, approveOrder, getOrderInfo, getOrders, ManufacturingOrder, ManufacturingOrderRevision } from "../../api/product-api";
import { route } from "../../App";
import { Alert } from "../../components/Alert";
import { ScanPackQR } from "../../components/ScanPackQR";
import { Snackbar, SnackbarShowCommand } from "../../components/Snackbar";
import { useCommand } from "../../hooks";
import { parseToDateAndFormatIfExists, trimText } from "../../utils";
import { TrimmedText } from "../../components/TrimmedText";
import "./styles.scss";
import { jsPDF } from "jspdf";
import { stellaBIO } from "../../data/serialNumberSearchExecs";

function group<T>(arr: T[], groupKey: (v: T) => string, keyMap: (v: T) => string) {
    const groups = [];
    const groupsMap: { [key: string]: T[] } = {};

    for (let i = 0; i < arr.length; i++) {
        const item = arr[i];
        const key = groupKey(item);

        if (!groupsMap[key]) {
            groupsMap[key] = [];
            groups.push({ key: keyMap(item), items: groupsMap[key] });
        }

        groupsMap[key].push(item);
    }

    return groups;
}

function PDFRoot(props: { children: ComponentChildren, onDone: () => void }) {
    useEffect(() => {
        props.onDone();
    });

    return <div style="flex: 1 1; display: flex;">{props.children}</div>;
}

async function downloadPdf(order: ManufacturingOrder, revision: ManufacturingOrderRevision) {
    var doc = new jsPDF({
        unit: "px",
        orientation: "landscape",
    })

    const root = document.createElement("div");
    root.style.display = "flex";
    root.style.width = "632px";
    root.style.minHeight = "440px";
    root.style.color = "black";
    root.style.fontSize = "8.7px";
    root.style.padding = "40px";
    root.style.boxSizing = "border-box";
    root.style.fontFamily = ""

    const date = new Date();

    render(<PDFRoot onDone={async () => {
        // document.body.appendChild(root);
        await doc.html(root, { });
        doc.save("Order-" + order.id + ".pdf");
    }}>
        <div style="display: flex; flex-direction: column; flex: 1 1;">
            <div style="margin: 5px;">Order {order.name} #{order.id} rev {revision.version}</div>
            <div style="margin-left: 10px; margin-right: 10px;">
                <table style="border: 1px solid black; border-bottom: none; border-right: none; border-spacing: none; border-collapse: collapse; width: 100%;">
                    <thead style="background-color: #0099E0; color: #ffffff; font-weight: 700;">
                        <th style="border-right: 1px solid black; border-bottom: 1px solid black; padding: 3px">BOX</th>
                        <th style="border-right: 1px solid black; border-bottom: 1px solid black; padding: 3px">No.</th>
                        <th style="border-right: 1px solid black; border-bottom: 1px solid black; padding: 3px">Type</th>
                        <th style="border-right: 1px solid black; border-bottom: 1px solid black; padding: 3px">Serial Number</th>
                        <th style="border-right: 1px solid black; border-bottom: 1px solid black; padding: 3px">Quantity</th>
                        <th style="border-right: 1px solid black; border-bottom: 1px solid black; padding: 3px">Description</th>
                        <th style="border-right: 1px solid black; border-bottom: 1px solid black; padding: 3px">Packed</th>
                    </thead>
                    {revision.manufacturingOrderRevisionProducts!.map((v, i) => <tr>
                        <td style={"text-align: center; border-right: 1px solid black;" + (i === revision.manufacturingOrderRevisionProducts!.length - 1 ? "border-bottom: 1px solid black;" : "")}>
                            {i === 0 ? "1" : ""}
                        </td>
                        <td style="padding: 2px 2px; text-align: center; border-right: 1px solid black; border-bottom: 1px solid black;">{i + 1}</td>
                        <td style="padding: 2px 2px; border-right: 1px solid black; border-bottom: 1px solid black;">{v.product.productTypeRevision!.productType!.name}</td>
                        <td style="padding: 2px 2px; border-right: 1px solid black; border-bottom: 1px solid black;">{v.product.serialNumber}</td>
                        <td style="padding: 2px 2px; text-align: center; border-right: 1px solid black; border-bottom: 1px solid black;">{v.quantity}</td>
                        <td style="padding: 2px 2px; border-right: 1px solid black; border-bottom: 1px solid black;">{v.product.productTypeRevision!.productType!.brand}</td>
                        <td style="padding: 2px 2px; text-align: center; border-right: 1px solid black; border-bottom: 1px solid black;"><span style="display: inline-block; width: 8px; height: 8px; border-radius: 0; border: 1px solid #4f4f4f;"/></td>
                    </tr>)}
                </table>
            </div>
            <div style="text-align: right; margin-right: 10px;">Generated: {date.getFullYear()}-{(date.getMonth() + 1).toString().padStart(2, "0")}-{date.getDay().toString().padStart(2, "0")} {date.getHours().toString().padStart(2, "0")}:{date.getMinutes().toString().padStart(2, "0")}</div>
        </div>
    </PDFRoot>, root);
}

export function Order(props: RoutableProps & { id?: string, rev?: string }) {
    const [orderData, setOrderData] = useState(null as null | Required<ManufacturingOrder>);
    const [orders, setOrders] = useState(null as null | (ManufacturingOrder & { manufacturingOrderRevisions: ManufacturingOrderRevision[] })[]);
    const [orderRevision, setOrderRevision] = useState(null as null | ManufacturingOrderRevision);
    const [ordersSearch, setOrdersSearch] = useState("");
    const [searchPopupVisible, setSearchPopupVisible] = useState(false);
    const [serialSearch, setSerialSearch] = useState("");
    const [serialEffectiveSearch, setSerialEffectiveSearch] = useState("");
    const [serialSearchStatus, setSerialSearchStatus] = useState("");
    const showSnackbarCommand = useCommand<SnackbarShowCommand>();

    useEffect(() => {
        setOrderData(null);
        setOrders(null);

        if (!props.id) {
            getOrders().then(async (orders) => {

                // for (let i = 0; i < orders.length; i++) {
                //     orders[i].createdAt = new Date(Date.parse(orders[i].createdAt + " UTC"));
                //     orders[i].approvedAt = orders[i].approvedAt ? new Date(Date.parse(orders[i].approvedAt + " UTC")) : null;
                // }

                orders.forEach(o => o.manufacturingOrderRevisions.sort((a, b) => b.version - a.version));
                setOrders(orders);
            });

            return;
        }

        let id: string | number = Number(props.id);

        if (isNaN(id)) {
            id = props.id;
        }

        getOrderInfo(id).then(async (order) => {
            order.manufacturingOrderRevisions.sort((a, b) => b.version - a.version);
            setOrderData(order);
        });
    }, [props.id]);

    useEffect(() => {
        if (orderData) {
            const rev = props.rev;

            if (!rev || isNaN(+rev)) {
                setOrderRevision(orderData.manufacturingOrderRevisions[0]);
            }
            else {
                const currentRevision = orderData.manufacturingOrderRevisions.find(v => v.version === (+rev));
                setOrderRevision(currentRevision ?? null);
            }

            return;
        }

        setOrderRevision(null);
    }, [orderData, props.id, props.rev]);

    useEffect(() => {
        setSerialSearchStatus("");
        setSerialEffectiveSearch("");

        if (!serialEffectiveSearch) {
            return;
        }

        getOrderInfo(serialEffectiveSearch).then(async (order) => {
            route(`/order/${serialEffectiveSearch}`);
        }).catch((reason) => {
            if (reason instanceof ApiError) {
                if (reason.statusCode < 400 || reason.statusCode >= 500) {
                    setSerialSearchStatus("Błąd podczas komunikacji z bazą danych.");
                }
                else {
                    setSerialSearchStatus("Ten numer seryjny nie znajduje się w żadnym zestawieniu.");
                }
            } else {
                setSerialSearchStatus("Brak połączenia z bazą danych.");
            }

            return;
        });
    }, [serialEffectiveSearch]);

    if (!orderData && !orders) {
        return <div style="display: flex; flex-direction: column; justify-content: center; align-items: center; width: 100%; height: 100%">
            <div>Pobieranie danych...</div>
            <div class="loader"></div>
        </div>;
    }

    return <div class="order-page">
        <Snackbar doShow={showSnackbarCommand}/>
        {orderData && orderRevision ? [
            <h1>
                <div style="display: flex;">
                    <div style="flex: 1 1;">Zestawienie {orderData.name}</div>
                    <div>Rewizja: {orderRevision.version}</div>
                </div>
                {orderRevision.approvedBy ? <div style="font-size: 12px; font-weight: normal">Zatwierdzone przez {orderRevision.approvedBy?.email}</div> : null}
                {orderRevision.otherData?.asanaLink ? <div style="font-size: 12px; font-weight: normal">Asana: <a href={orderRevision.otherData?.asanaLink}>{orderRevision.otherData?.asanaLink}</a></div> : null}
                {orderRevision.otherData?.jiraLink ? <div style="font-size: 12px; font-weight: normal">Jira: <a href={orderRevision.otherData?.jiraLink}>{orderRevision.otherData?.jiraLink}</a></div> : null}
                {orderRevision.otherData?.notes ? <div style="margin-top: 10px; font-size: 12px; font-weight: normal">
                    <div>Notatki:</div>
                    <div>{orderRevision.otherData?.notes}</div>
                </div> : null}
            </h1>,
            <div class="element-list paper">
                <div style="display: flex">
                    <h2 style="flex: 1 1 0">Rewizje</h2>
                    {orderRevision.approvedBy && orderData.manufacturingOrderRevisions[0].id === orderRevision.id ?
                        <button type="button" class="primary" style="margin-bottom: 10px;" onClick={() => route("/order/revision/" + orderData.id)}>Utwórz rewizję</button>
                        : null}
                </div>
                <table class="orders selectable list secondary">
                    <thead>
                        <tr>
                            <th class="header">Rewizja</th>
                            <th class="header">Nazwa</th>
                            <th class="header">Rejestrujący</th>
                            <th class="header">Data rejestracji</th>
                            <th class="header">Zatwierdzone przez</th>
                            <th class="header">Data zatwierdzenia</th>
                        </tr>
                    </thead>
                    <tbody>
                        {orderData.manufacturingOrderRevisions.map((item, i) => [
                            <tr class={item === orderRevision ? "item selected" : "item"} onClick={() => route(`/order/${orderData.id}/${item.version}`, true)}>
                                <td>{item.version}</td>
                                <td>{item.description}</td>
                                <td>{item.creator.email}</td>
                                <td>{parseToDateAndFormatIfExists(item.createdAt)}</td>
                                <td>{item.approvedBy?.email}</td>
                                <td>{parseToDateAndFormatIfExists(item.approvedAt)}</td>
                            </tr>
                        ])}
                    </tbody>
                </table>
            </div>,
            <div class="element-list paper" style="margin-top: 10px;">
                <h2>Elementy</h2>
                <table class="elements">
                    <thead>
                        <tr>
                            <th class="header">Lp.</th>
                            <th class="header">Typ elementu</th>
                            <th class="header">Numer seryjny</th>
                            <th class="header">Opis</th>
                            <th class="header">Ilość</th>
                            <th class="header">Rejestrujący</th>
                            <th class="header">Data rejestracji</th>
                        </tr>
                    </thead>
                    <tbody>
                        {orderRevision.manufacturingOrderRevisionProducts!.map((item, i) => [
                            <tr>
                                <td>{i + 1}</td>
                                <td>{item.product.productTypeRevision!.productType!.brand}</td>
                                <td>{item.product.serialNumber}</td>
                                <td>
                                    <TrimmedText maxLength={50} tooltip={true}>{item.product.description}</TrimmedText>
                                </td>
                                <td>{item.quantity}</td>
                                <td>{item.product.creator.email}</td>
                                <td>{parseToDateAndFormatIfExists(item.product.createdAt)}</td>
                            </tr>
                        ])}
                    </tbody>
                </table>
                <div class="buttons">
                    <button type="button" class="info" onClick={async () => {
                        await navigator.clipboard.writeText(orderRevision.manufacturingOrderRevisionProducts!.map((item) => `${item.product.productTypeRevision!.productType!.brand}\t${item.product.serialNumber}`).join("\n"));
                        showSnackbarCommand.run("Skopiowano", "info", "info", 2000);
                    }}>
                        <span class="material-icons" style="font-size: 17px; margin-right: 5px">content_copy</span>
                        Skopiuj dwie pierwsze kolumny
                    </button>
                    <button class="secondary" onClick={() => downloadPdf(orderData, orderRevision)}>
                        <span class="material-icons" style="font-size: 17px; margin-right: 5px">picture_as_pdf</span>
                        Eksportuj PDF
                    </button>
                    <div class="fill"/>
                    {!orderRevision.approvedBy ? [
                        <button type="button" class="success" onClick={async () => {
                            if (!orderData || !orderData.id) {
                                showSnackbarCommand.run("Nie udało się zatwierdzić zestawienia", "error", "error");
                                return;
                            }

                            let data;

                            try {
                                data = await approveOrder(orderData.id);
                            }
                            catch(ex) {
                                console.log(ex);
                                showSnackbarCommand.run("Nie udało się zatwierdzić zestawienia", "error", "error");
                                return;
                            }

                            showSnackbarCommand.run("Zatwierdzono pomyślnie", "success", "check");

                            const order = await getOrderInfo(orderData.id);
                            order.manufacturingOrderRevisions.sort((a, b) => b.version - a.version);
                            setOrderData(order);
                        }}>
                            <span class="material-icons" style="font-size: 17px; margin-right: 5px">check</span>
                            Zatwierdź
                        </button>,
                        <button type="button" class="info" onClick={() => route("/order/edit/" + orderData.id)}>
                            <span class="material-icons" style="font-size: 17px; margin-right: 5px">edit</span>
                            Edytuj
                        </button>
                    ] : null}
                </div>
            </div>
        ] : orders ? [
            <h1>Zestawienia</h1>,
            <div class="order-items paper">
                <div class="search">
                    <input class="search-text" placeholder="Wyszukaj..." onInput={(e) => setOrdersSearch(e.currentTarget.value.toLowerCase())}/>
                    <label class="button search-serial" for="popup-visible-controller-order">Wyszukaj po numerze seryjnym</label>
                    <input type="checkbox" class="popup-visible" onChange={(e) => setSearchPopupVisible(e.currentTarget.checked)}  id="popup-visible-controller-order"/>
                    <div class="popup">
                        <label class="background" for="popup-visible-controller-order"></label>
                        <div class="content" style="display: flex; flex-direction: column; padding: 10px;">
                            <div class="serial-popup">
                                <h1>Wpisz numer seryjny</h1>
                                <ScanPackQR connectionCondition={searchPopupVisible} onRead={(data) => {
                                    const text = JSON.parse(data) as string;
                                    let match = stellaBIO.execs.elementSerialRegex.exec(text) as string[] | null;

                                    if (!match || match.length < 2) {
                                        match = stellaBIO.execs.elementSerialRegexGs1.exec(text);
                                    }

                                    if (!match || match.length < 2) {
                                        return;
                                    }

                                    const serial = match[1];
                                    setSerialSearch(serial);
                                    setSerialEffectiveSearch(serial);
                                }}/>
                                <div class="user">
                                    <input class="search-text" placeholder="Numer seryjny" value={serialSearch} onChange={(e) => {
                                        setSerialSearch(e.currentTarget.value);
                                        setSerialEffectiveSearch("");
                                    }}/>
                                    <button onClick={() => {
                                        setSerialEffectiveSearch(serialSearch);
                                    }}>Wyszukaj</button>
                                </div>
                                {serialSearchStatus ?
                                    <div class="status">
                                        <Alert type="error">{serialSearchStatus}</Alert>
                                    </div> : null}
                            </div>
                        </div>
                    </div>
                </div>

                    <table class="orders selectable" style="margin-bottom: 10px;">
                        <thead>
                            <tr>
                                <th class="header">Id</th>
                                <th class="header">Rodzaj</th>
                                <th class="header">Nazwa</th>
                                <th class="header">Rejestrujący</th>
                                <th class="header">Data rejestracji</th>
                                <th class="header">Zatwierdzone przez</th>
                                <th class="header">Data zatwierdzenia</th>
                                <th class="header">Rewizja</th>
                            </tr>
                        </thead>
                        <tbody>
                        {group(
                            orders.filter(item => !ordersSearch
                                || item.name.toLowerCase().indexOf(ordersSearch) >= 0
                                || item.description.toLowerCase().indexOf(ordersSearch) >= 0
                                || item.creator.email.toLowerCase().indexOf(ordersSearch) >= 0
                                || item.manufacturingOrderRevisions[0].description?.toLowerCase().indexOf(ordersSearch) >= 0
                                || parseToDateAndFormatIfExists(item.createdAt).toLocaleString().indexOf(ordersSearch) >= 0),
                            (v) => new Date(Date.parse(v.createdAt)).getMonth().toString() + "-" + new Date(Date.parse(v.createdAt)).getFullYear().toString(),
                            (v) => new Date(Date.parse(v.createdAt)).toLocaleString("pl-pl", { month: "long", year: "numeric" })
                        ).map((group) => [
                            <tr style="background-color: #3f3f3f; cursor: default;">
                                <td colSpan={8}>{group.key}</td>
                            </tr>,
                            ...group.items.map((item, i) =>
                                <tr onClick={() => route(`/order/${item.id}`)}>
                                    <td>{item.id}</td>
                                    <td>{item.name}</td>
                                    <td>{item.manufacturingOrderRevisions[0].description ?? ""}</td>
                                    <td>{item.creator.email}</td>
                                    <td>{parseToDateAndFormatIfExists(item.createdAt)}</td>
                                    <td>{item.manufacturingOrderRevisions[0].approvedBy?.email}</td>
                                    <td>{parseToDateAndFormatIfExists(item.manufacturingOrderRevisions[0].approvedAt)}</td>
                                    <td>{item.manufacturingOrderRevisions[0].version}</td>
                                </tr>
                            )
                        ])}
                    </tbody>
                </table>
            </div>
        ] : null}
    </div>;
}
